import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Users as UsersIcon, Target as TargetIcon } from "lucide-react";

export default function LeadsSummary({ users = [], refreshKey = 0 }) {
  const [period, setPeriod] = useState("current_month");
  const [ownerId, setOwnerId] = useState("all");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      const r = await api.get(`/leads/summary?period=${period}&owner_id=${ownerId}`);
      setData(r.data);
      setExpanded({});
    })();
  }, [period, ownerId, refreshKey]);

  const toggle = (oid) => setExpanded((p) => ({ ...p, [oid]: !p[oid] }));

  if (!data) return null;

  return (
    <Card className="border-slate-200 mb-5" data-testid="leads-summary">
      <CardContent className="p-5 space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
              <TargetIcon className="h-3 w-3" /> Leads generated
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="font-heading font-bold text-4xl text-slate-900 tabular-nums" data-testid="leads-summary-total">
                {data.total_count}
              </div>
              <div className="text-xs text-slate-500">{data.period_label}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {[
              { v: "current_month", label: "Current month" },
              { v: "current_quarter", label: "Current quarter" },
              { v: "current_year", label: "Year to date" },
              { v: "last_30", label: "Last 30 days" },
              { v: "all", label: "All time" },
            ].map((p) => (
              <button
                key={p.v}
                data-testid={`leads-summary-period-${p.v}`}
                onClick={() => setPeriod(p.v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  period === p.v ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
              >{p.label}</button>
            ))}
            <select
              data-testid="leads-summary-owner"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="bg-white border border-slate-200 rounded-md px-2.5 py-1.5 text-xs font-medium"
            >
              <option value="all">All owners</option>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
            </select>
          </div>
        </div>

        {/* Owner breakdown */}
        {data.by_owner.length > 0 && (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="leads-summary-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2 w-6"></th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2 text-right">Leads</th>
                  <th className="px-3 py-2">Status mix</th>
                </tr>
              </thead>
              <tbody>
                {data.by_owner.map((o) => {
                  const open = expanded[o.owner_id];
                  const statusCounts = {};
                  o.leads.forEach((l) => {
                    const s = l.status || "Unqualified";
                    statusCounts[s] = (statusCounts[s] || 0) + 1;
                  });
                  return (
                    <React.Fragment key={o.owner_id}>
                      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => toggle(o.owner_id)} data-testid={`leads-summary-row-${o.owner_id}`}>
                        <td className="px-3 py-2">
                          {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <UsersIcon className="h-3.5 w-3.5 text-slate-400" />
                            <span className="font-medium">{o.owner_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums">{o.count}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(statusCounts).map(([s, n]) => (
                              <Badge key={s} variant="outline" className="text-[9px] py-0">
                                {s} {n}
                              </Badge>
                            ))}
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-blue-50/30">
                          <td colSpan={4} className="px-6 py-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-[10px] uppercase text-slate-500">
                                  <th className="py-1">Name</th>
                                  <th className="py-1">Company</th>
                                  <th className="py-1">Status</th>
                                  <th className="py-1">Industry</th>
                                  <th className="py-1">Created</th>
                                </tr>
                              </thead>
                              <tbody>
                                {o.leads.map((l) => (
                                  <tr key={l.id} className="border-t border-blue-100">
                                    <td className="py-1.5">{l.first_name} {l.last_name}</td>
                                    <td className="py-1.5 text-slate-600">{l.company}</td>
                                    <td className="py-1.5"><Badge variant="outline" className="text-[9px]">{l.status}</Badge></td>
                                    <td className="py-1.5 text-slate-500">{l.industry || l.interested_service || "—"}</td>
                                    <td className="py-1.5 font-mono text-slate-400">{new Date(l.created_at).toLocaleDateString("en-GB")}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
